import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listActividad, type ActividadEntry } from '@lib/services/actividad.service';
import { useAuthStore } from '@lib/store/auth.store';
import { isMedicus } from '@lib/types';
import { TableSkeleton } from '@components/medicus-ui/Loaders';

function describeEvent(e: ActividadEntry): string {
  const map: Record<string, string> = {
    'auth.login.ok': 'Inicio de sesión exitoso',
    'auth.login.fallido': 'Intento de login fallido',
    'auth.activate': 'Activación de cuenta',
    'padron.crear': 'Padrón creado',
    'padron.cerrar': 'Padrón cerrado',
    'padron.separar-inconsistencias': 'Separación de inconsistencias',
    'padron.subir-correcciones': 'Carga de correcciones',
    'asociado.actualizar': 'Asociado editado',
    'asociado.eliminar': 'Asociado eliminado',
    'usuario.invitar': 'Invitación de usuario',
    'usuario.actualizar': 'Usuario actualizado',
    'usuario.eliminar': 'Usuario eliminado',
    'empresa.crear': 'Organización creada',
    'empresa.actualizar': 'Organización actualizada',
    'empresa.eliminar': 'Organización eliminada',
    'legajo.tmk.oferta-enviada': 'TMK · Oferta MS2 enviada',
    'legajo.tmk.oferta-aceptada-por-titular': 'TMK · Oferta MS2 aceptada por el titular',
    'legajo.tmk.contacto-resuelto': 'TMK · Contacto resuelto',
    'legajo.tmk.lead-reclamado': 'TMK · Contacto reclamado',
    'legajo.tmk.lead-liberado': 'TMK · Contacto liberado',
    'legajo.tmk.lead-reasignado': 'TMK · Contacto reasignado',
    'legajo.tmk.interaccion': 'TMK · Interacción registrada',
  };
  return map[`${e.entidad}.${e.accion}`] ?? `${e.entidad} · ${e.accion}`;
}

export function ActividadPage() {
  const { user } = useAuthStore();
  const [entidad, setEntidad] = useState<string>('');
  const [empresaIdFilter, setEmpresaIdFilter] = useState<string>('');

  const actividad = useQuery({
    queryKey: ['actividad', entidad, empresaIdFilter],
    queryFn: () =>
      listActividad({
        entidad: entidad || undefined,
        empresaId: empresaIdFilter || undefined,
        limit: 300,
      }),
    refetchInterval: 5000,
  });

  if (!user) return null;
  const esAdmin = isMedicus(user.rol);

  return (
    <section>
      <header className="pageHeader">
        <h1 className="heading-3">Actividad</h1>
        <p className="text-element-mid">
          {esAdmin
            ? 'Bitácora completa del sistema. Cualquier usuario y organización.'
            : 'Bitácora de las acciones realizadas dentro de tu organización.'}
        </p>
      </header>

      <div className="card" style={{ marginTop: '1rem' }}>
        <div className="rowBetween" style={{ marginBottom: '0.75rem', gap: 12 }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 12, color: 'var(--color-element-mid)' }}>Entidad</span>
              <select
                className="input"
                value={entidad}
                onChange={(e) => setEntidad(e.target.value)}
                style={{ minWidth: 180 }}
              >
                <option value="">(todas)</option>
                <option value="auth">auth</option>
                <option value="padron">padron</option>
                <option value="asociado">asociado</option>
                <option value="usuario">usuario</option>
                <option value="empresa">organización</option>
              </select>
            </label>

            {esAdmin && (
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 12, color: 'var(--color-element-mid)' }}>
                  Organización ID (filtro)
                </span>
                <input
                  className="input"
                  value={empresaIdFilter}
                  onChange={(e) => setEmpresaIdFilter(e.target.value)}
                  placeholder="UUID de organización o vacío"
                  style={{ minWidth: 280 }}
                />
              </label>
            )}
          </div>
          <p className="text-element-mid" style={{ fontSize: 13 }}>
            {actividad.data?.total ?? 0} eventos
          </p>
        </div>

        {actividad.isLoading && <TableSkeleton rows={10} />}
        {actividad.isError && (
          <p className="status status-error">No se pudo cargar la actividad.</p>
        )}

        {actividad.data && actividad.data.items.length === 0 && (
          <p className="text-element-mid">No hay eventos registrados todavía.</p>
        )}

        {actividad.data && actividad.data.items.length > 0 && (
          <div className="tableWrap">
            <table className="dataTable">
              <thead>
                <tr>
                  <th style={{ width: 170 }}>Fecha</th>
                  <th>Evento</th>
                  <th>Usuario</th>
                  {esAdmin && <th>Organización</th>}
                  <th>Entidad afectada</th>
                  <th>Detalle</th>
                </tr>
              </thead>
              <tbody>
                {actividad.data.items.map((e, i) => (
                  <tr key={e.id ?? i}>
                    <td style={{ fontSize: 12, color: 'var(--color-element-mid)' }}>
                      {e.createdAt ? new Date(e.createdAt).toLocaleString('es-AR') : '—'}
                    </td>
                    <td>
                      <strong>{describeEvent(e)}</strong>
                      <div style={{ fontSize: 11, color: 'var(--color-element-mid)', fontFamily: 'monospace' }}>
                        {e.entidad}.{e.accion}
                      </div>
                    </td>
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {e.usuarioId ? e.usuarioId.slice(0, 8) + '…' : '—'}
                    </td>
                    {esAdmin && (
                      <td style={{ fontFamily: 'monospace', fontSize: 12 }}>
                        {e.empresaId ? e.empresaId.slice(0, 8) + '…' : '—'}
                      </td>
                    )}
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {e.entidadId ? e.entidadId.slice(0, 8) + '…' : '—'}
                    </td>
                    <td>
                      <details>
                        <summary
                          style={{
                            cursor: 'pointer',
                            color: 'var(--color-primary-darker)',
                            fontSize: 12,
                          }}
                        >
                          Ver before/after
                        </summary>
                        <pre
                          style={{
                            fontSize: 11,
                            background: 'var(--color-surface-accent)',
                            padding: 8,
                            marginTop: 4,
                            borderRadius: 4,
                            maxWidth: 600,
                            overflow: 'auto',
                          }}
                        >
                          {JSON.stringify({ before: e.before, after: e.after }, null, 2)}
                        </pre>
                      </details>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
